import { useEffect, useRef, useState } from 'react';
import { Download, Upload } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import { Button, Skeleton } from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useGlobalConfig,
  useUpdateGlobalConfig,
  withConfig,
  useStaticFiles,
  useUploadStaticFile,
  useDeleteStaticFile,
} from '@/api/waap';
import { errMessage } from '../waap-util';
import { Labeled, SaveBar, ToggleRow, downloadText } from './settings-ui';
import styles from './WebAccessControlSettings.module.css';

// The vendor's static-file type for the account IP blacklist (typo preserved
// verbatim — the portal stores it as "global_balck_ip_list").
const BLACKLIST_TYPE = 'global_balck_ip_list';

const SAMPLE = ['203.0.113.0/24', '198.51.100.7', '2001:db8::/32'].join('\n') + '\n';

/**
 * Web Access Control → IP Blacklist. The enable toggle lives in the account
 * global-config (`enable_black_list`); the blocked IPs are an uploaded .txt file
 * (one IP/CIDR per line, ≤100k entries) managed via the shared static-files API.
 */
export function WebAccessControlSettings({ domainId }: { domainId: string }) {
  const { show } = useToast();
  const { data: record, isLoading } = useGlobalConfig(domainId);
  const { data: files = [] } = useStaticFiles(domainId, BLACKLIST_TYPE);

  const [enabled, setEnabled] = useState(false);
  // Seed from the server only when the record actually changes (by `updated`),
  // so a background refetch can't clobber an unsaved toggle.
  const syncedUpdated = useRef<number | null>(null);
  useEffect(() => {
    if (record && record.updated !== syncedUpdated.current) {
      syncedUpdated.current = record.updated;
      setEnabled(record.config.enable_black_list);
    }
  }, [record]);

  const fileInput = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<{ content: string; name: string } | null>(null);

  const update = useUpdateGlobalConfig(domainId, {
    onSuccess: () => show({ tone: 'success', title: 'Web Access Control saved' }),
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });

  const upload = useUploadStaticFile(domainId, {
    onSuccess: (_d, vars) => show({ tone: 'success', title: `Uploaded ${vars.name}` }),
    onError: (e) => show({ tone: 'danger', title: 'Upload failed', description: errMessage(e) }),
  });
  const del = useDeleteStaticFile(domainId, BLACKLIST_TYPE);

  const doUpload = (p: { content: string; name: string }) =>
    upload.mutate({ content: p.content, name: p.name, type: BLACKLIST_TYPE });

  const onPickFile = async (file: File) => {
    if (!/\.txt$/i.test(file.name)) {
      show({ tone: 'danger', title: 'Only .txt files are supported' });
      return;
    }
    const content = await file.text();
    const p = { content, name: file.name };
    if (files.length > 0) setPending(p);
    else doUpload(p);
  };

  const confirmReplace = async () => {
    if (!pending) return;
    try {
      await Promise.all(files.map((f) => del.mutateAsync(f.id)));
      doUpload(pending);
    } catch (e) {
      show({ tone: 'danger', title: 'Replace failed', description: errMessage(e) });
    } finally {
      setPending(null);
    }
  };

  const save = () => {
    if (!record) return;
    update.mutate(withConfig(record, { enable_black_list: enabled }));
  };

  if (isLoading) return <Skeleton className={styles.skeleton} />;

  return (
    <Section
      title="IP Blacklist"
      description={
        <>
          This feature allows you to upload a file containing a list of IPs that need to be
          blocked. Requests from this list will be blocked.{' '}
          <Button variant="ghost" size="sm" onClick={() => downloadText('ip_blacklist_sample.txt', SAMPLE)}>
            Download sample
          </Button>
        </>
      }
    >
      <ToggleRow
        title="Enable"
        description="Block requests from the IPs in the uploaded blacklist file."
        checked={enabled}
        onChange={setEnabled}
      />

      {enabled && (
        <div className={styles.fields}>
          <ol className={styles.rules}>
            <li>Only TXT file can be uploaded and it must not contain more than 100,000 data entries.</li>
            <li>Each IP or network segment in the file should be positioned in a separate line.</li>
            <li>
              Both IPv4 and IPv6 addresses can be provided in CIDR (IPv6 addresses in mixed notation
              are not supported).
            </li>
          </ol>

          {files.length > 0 && (
            <Labeled label="Current file">
              <span className={styles.fileName}>{files[0].name}</span>
            </Labeled>
          )}

          <div className={styles.actions}>
            <Button
              variant="secondary"
              startIcon={<Download size={14} />}
              onClick={() => downloadText('ip_blacklist_sample.txt', SAMPLE)}
            >
              Download
            </Button>
            <input
              ref={fileInput}
              type="file"
              accept=".txt,text/plain"
              className={styles.hiddenInput}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onPickFile(f);
                e.target.value = '';
              }}
            />
            <Button
              variant="secondary"
              startIcon={<Upload size={14} />}
              disabled={upload.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {upload.isPending ? 'Uploading…' : 'Upload'}
            </Button>
          </div>
        </div>
      )}

      <SaveBar onSave={save} saving={update.isPending} disabled={!record} />

      <ConfirmDialog
        open={!!pending}
        onOpenChange={(o) => !o && setPending(null)}
        title="Replace existing blacklist file?"
        description={`A blacklist file already exists. Uploading "${pending?.name}" will replace it.`}
        confirmLabel="Replace"
        loading={del.isPending || upload.isPending}
        onConfirm={confirmReplace}
      />
    </Section>
  );
}
